import { EnumCommodity, EnumTipoDocumento } from '../../api'
import { CUPSModel } from '../../api/Argentia/model/contracts'

export interface CupsProcedureForm extends Pick<CUPSModel, 'id' | 'commodity'> {
  cupsCode?: string
  cnaeCode?: string
  toll?: number
  qa?: number
  qd?: number
  pressureGroupId?: number
  changeReason?: string
}

export interface ProcedureFormData {
  type?:
    | EnumProcedureType
    | EnumProcedureTypeGas
    | EnumProcedureTypePower
    | EnumComplaintTypeInvoice
    | EnumComplaintTypeProcedure
    | EnumProcedureTypeHidden
  contractId?: number
  cupsCode?: string
  toll?: number
  commodity?: EnumCommodity
  context?: EnumProcedureProcess
  qaEstimated?: number
  qdHired?: number
  cnaeId?: number
  cnaeCode?: string
  efectiveDate?: EnumEffectiveDate
  date?: Date
  newDate?: Date
  conditionalRegistration?: boolean
  socialSubsidy?: boolean
  essentialIndicator?: string
  essentialMovementDate?: Date
  essentialIndicatorGas?: string
  protectedIndicator?: string
  reason?: EnumProcedureReason
  reasonChangeOwner?: EnumProcedureReasonChangeOwner
  reasonTermination?: EnumReasonTermination
  reasonGasSuspension?: string
  requestType?: EnumRequestType
  administrativeRequest?: EnumAdministrativeRequest
  residential?: EnumResidential
  subrogation?: boolean
  principalResidence?: boolean
  qDiario?: number
  gasUsage?: EnumGasUsage
  deviceTransfer?: EnumDeviceTransfer
  tension?: number
  /* -- CUPS complaint -- */
  complaintType?: number
  complaintSubType?: string
  contractConcept?: string
  billingConcept?: string
  comments?: string
  complaintOwnerType?: number
  startDate?: Date
  endDate?: Date
  deadline?: Date
  priority?: boolean
  /* -- INVOICE complaint -- */
  invoiceId?: number
  invoiceCode?: string
  /* -- PROCEDURE complaint -- */
  originProcedureId?: number
  procedureCode?: string
  /* -- NEW_CAPACITY -- */
  capacityId?: number
  /* -- ADMINISTRATIVE_CHANGES -- */
  changeReason?: string
  admChangeReasons?: string
}

export type NewProcedureRequestFormData = ProcedureCreateCommonParams & ProcedureCreateOptParams

export interface ProcedureCreateCommonParams {
  cupsCode: string
  clientCode: string
  commodity: EnumCommodity
  type: EnumProcedureType
  context?: EnumProcedureProcess

  qaEstimated?: number
  qdHired?: number
  cnaeCode?: string
  toll?: number
  changeReason?: string
}

export interface ProcedureCreateOptParams {
  efectiveDate: EnumEffectiveDate
  date?: Date
  conditionalRegistration?: boolean
  socialSubsidy?: boolean
  toll?: number
  reason?: EnumProcedureReason
  reasonTermination?: EnumReasonTermination
  reasonGasSuspension?: string
  requestType?: EnumRequestType
  administrativeRequest?: EnumAdministrativeRequest
  reasonChangeOwner?: EnumProcedureReasonChangeOwnerPower
  residential?: EnumResidential
  subrogation?: boolean
  principalResidence?: boolean
  qDiario?: number
  gasUsage?: EnumGasUsage
  deviceTransfer?: EnumDeviceTransfer
  requestedPressure?: number
  meterProperty?: EnumMeterProperty
  gasStationType?: EnumGasStationType
  tension?: number
  newDate?: Date
  essentialIndicator?: string
  essentialMovementDate?: Date
  essentialIndicatorGas?: string
  protectedIndicator?: string
  /* -- CUPS complaint -- */
  complaintType?: number
  complaintSubType?: string
  contractConcept?: string
  invoiceConcept?: string
  incidentCode?: string
  comments?: string
  complaintOwnerType?: number
  startDate?: Date
  endDate?: Date
  deadline?: Date
  priority?: boolean
  /* -- INVOICE complaint -- */
  invoiceId?: number
  /* -- PROCEDURE complaint -- */
  originProcedureId?: number
  /* -- NEW_CAPACITY -- */
  capacityId: number
  /* -- ADMINISTRATIVE_CHANGES -- */
  admChangeReasons?: string
  billingFrequency?: string
}

export namespace ProcedureFormMode {
  export const ALTA_CONTRATO_GAS = 'ALTA_CONTRATO_GAS'
  export const ALTA_CONTRATO_POWER = 'ALTA_CONTRATO_POWER'
  export const CAMBIO_TITULAR_POWER = 'CAMBIO_TITULAR_POWER'
  export const CAMBIO_TITULAR_ADM_TEC_POWER = 'CAMBIO_TITULAR_ADM_TEC_POWER'
  export const CAMBIO_TITULAR_GAS = 'CAMBIO_TITULAR_GAS'
  export const CAMBIO_TITULAR_ADM_TEC_GAS = 'CAMBIO_TITULAR_ADM_TEC_GAS'
  export const CAMBIO_CONTRATO_ATR = 'CAMBIO_CONTRATO_ATR'
  export const BAJA_CONTRATO_GAS = 'BAJA_CONTRATO_GAS'
  export const BAJA_CONTRATO_POWER = 'BAJA_CONTRATO_POWER'
  export const GENERAL = 'GENERAL'
  export const RECLAMACION_FACTURA = 'RECLAMACION_FACTURA'
  export const RECLAMACION_TRAMITACION = 'RECLAMACION_TRAMITACION'
  export const ALTA_CAPACIDAD = 'ALTA_CAPACIDAD'
  export const MODIFICACION_TECNICA = 'MODIFICACION_TECNICA'
  export const SUSPENSION_CONTRATO_GAS = 'SUSPENSION_CONTRATO_GAS'
  export const SUSPENSION_CONTRATO_POWER = 'SUSPENSION_CONTRATO_POWER'
  export const RENGANCHE_CONTRATO_GAS = 'REENGANCHE_CONTRATO_GAS'
}

export type ProcedureFormModeType = (typeof ProcedureFormMode)[keyof typeof ProcedureFormMode]

// Use only for form input.
export enum EnumProcedureProcess {
  'ALTA_CONTRATO_GAS' = 1,
  'ALTA_CONTRATO_POWER',
  'CAMBIO_TITULAR_GAS',
  'CAMBIO_TITULAR_ADM_TEC_GAS',
  'CAMBIO_TITULAR_POWER',
  'CAMBIO_CONTRATO_ATR',
  'BAJA_CONTRATO_GAS',
  'BAJA_CONTRATO_POWER',
  'GENERAL',
  'RECLAMACION_FACTURA_GAS',
  'RECLAMACION_FACTURA_POWER',
  'RECLAMACION_TRAMITACION_GAS',
  'RECLAMACION_TRAMITACION_POWER',
  'ALTA_CAPACIDAD_GAS',
  'MODIFICACION_TECNICA',
  'SUSPENSION_CONTRATO_GAS',
  'SUSPENSION_CONTRATO_POWER',
  'REENGANCHE_CONTRATO_GAS'
}

export enum EnumProcedureType {
  'NEW_WITHOUT_CHANGES' = 1,
  'NEW_WITH_CHANGES',
  'NEW_SUPPLY',
  'UPDATE_ATR',
  'UPDATE_OWNER_GAS',
  'CUPS',
  'ADMINISTRATIVE_TECHNICAL_CHANGE_PWR' = 20,
  'COMPLAINT_PROCEDURE_GAS' = 21,
  'COMPLAINT_INVOICE_GAS' = 22,
  'COMPLAINT_PROCEDURE_PWR' = 24,
  'COMPLAINT_INVOICE_PRW' = 25,
  'NEW_CAPACITY' = 27,
  'ADMINISTRATIVE_CHANGE' = 28,
  'ADMINISTRATIVE_CHANGE_PWR' = 18,
  'ACTION_GAS' = 36, // 'ACTUACION_GAS' Procedimiento 44
  'ADMINISTRATIVE_TECHNICAL_CHANGE_GAS' = 39,
  'UPDATE_OWNER_PWR' = 40,
  'SUSPEND_SUPPLY_GAS' = 41,
  'RECONNECT_SUPPLY_GAS' = 42,
  'SUSPEND_SUPPLY_PWR' = 43,
  'UPDATE_OWNER_ADM_TEC_GAS' = 44,
  'UPDATE_OWNER_ADM_TEC_PWR' = 45
}

export enum EnumProcedureTypeGas {
  'NEW_WITHOUT_CHANGES' = 1,
  'NEW_WITH_CHANGES',
  'NEW_SUPPLY',
  'UPDATE_OWNER' = 5,
  // 'TERMINATION' = 7,
  'CUPS' = 23,
  'NEW_CAPACITY' = 27,
  'ADMINISTRATIVE_CHANGE' = 28,
  'ACTION_GAS' = 36, // 'ACTUACION_GAS' Procedimiento 44
  'ADMINISTRATIVE_TECHNICAL_CHANGE_GAS' = 39,
  'SUSPEND_SUPPLY_GAS' = 41,
  'RECONNECT_SUPPLY_GAS' = 42
}

export enum EnumProcedureTypePower {
  'NEW_WITHOUT_CHANGES' = 1,
  'NEW_WITH_CHANGES',
  'NEW_SUPPLY',
  'UPDATE_ATR',
  // 'TERMINATION',
  'ADMINISTRATIVE_CHANGE_PWR' = 18,
  'ADMINISTRATIVE_TECHNICAL_CHANGE_PWR' = 20,
  'CUPS' = 26,
  'SUSPEND_SUPPLY_PWR' = 43
}

export enum EnumProcedureTypeHidden {
  'TERMINATION' = 1007,
  'TECHNICAL_CHANGE' = 1029
}

export enum EnumComplaintTypeInvoice {
  'COMPLAINT_INVOICE_GAS' = 22,
  'COMPLAINT_INVOICE_PWR' = 25
}

export enum EnumComplaintTypeProcedure {
  'COMPLAINT_PROCEDURE_GAS' = 21,
  'COMPLAINT_PROCEDURE_PWR' = 24
}

export enum EnumEffectiveDatePower {
  'READ_CYCLE' = 2,
  'SPECIFIC_DATE' = 4,
  'ASAP' = 5
}

export enum EnumEffectiveDate {
  'FIRST_POSSIBLE' = 1,
  'READ_CYCLE' = 2,
  'SPECIFIC_DATE' = 4,
  'ASAP' = 5
}

export enum EnumEffectiveDateValues {
  '01' = 1,
  '02' = 2,
  '04' = 4,
  '05' = 5
}

export enum EnumEffectiveDateValuesPower {
  'L' = 2, // EnumEffectiveDate.READ_CYCLE
  'F' = 4, // EnumEffectiveDate.SPECIFIC_DATE
  'A' = 5 // EnumEffectiveDate.ASAP
}

export enum EnumProcedureReason {
  'CHANGE_OWNER' = 1,
  'CHANGE_VOLUME',
  'BOTH'
}

export enum EnumProcedureReasonChangeOwner {
  'CHANGE_OWNER' = 1,
  'TRANSFER_OWNERSHIP' = 4,
  'SUBROGATED_OWNERSHIP_CHANGE' = 5
}

export enum EnumProcedureReasonChangeOwnerGas {
  'CHANGE_OWNER' = 1
}

export enum EnumProcedureReasonChangeOwnerPower {
  'TRANSFER_OWNERSHIP' = 4,
  'SUBROGATED_OWNERSHIP_CHANGE' = 5
}

export enum EnumGasUsage {
  'FUEL' = 1,
  'MOTOR_FUELS',
  'STATIONARY_ENGINES',
  'BONDED_WAREHOUSES'
}

export enum EnumGasUsageValues {
  '01' = 1,
  '02',
  '03',
  '04'
}

export enum EnumMeterProperty {
  'DEPOSIT' = 1,
  'RENTAL' = 2,
  'OWN_CASH' = 4,
  'OWN_SIX_MONTH' = 6,
  'OWN_TWELVE_MONTHS' = 8,
  'CUSTOMER_PROPERTY' = 10,
  'REPLACEMENT' = 11,
  'NEW' = 12
}

export enum EnumDeviceTransfer {
  'NA' = 99,
  'ONE',
  'TWO',
  'THREE',
  'FOUR',
  'FIVE',
  'SIX',
  'SEVEN',
  'EIGHT',
  'NINE',
  'TEN'
}

export enum EnumGasStationType {
  'NO' = 0,
  'PUBLIC_CNG_STATION',
  'PUBLIC_MIXED_STATION',
  'PRIVATE_CNG_STATION',
  'PRIVATE_MIXED_STATION',
  'CNG_STATION_WITH_CERTIFICATE'
}

export enum EnumGasStationTypeValues {
  '00' = 0,
  '01',
  '02',
  '03',
  '04',
  '05'
}

export enum EnumRequestType {
  'ADMINISTRATIVE' = 1,
  'TECHNICAL',
  'BOTH'
}

export enum EnumAdministrativeRequest {
  'TRANSFER_OWNERSHIP' = 1,
  'SUBROGATED_OWNERSHIP_CHANGE',
  'CORRECTION_OWNERS_DATA',
  'OTHER'
}

export enum EnumAdministrativeRequestValues {
  'T' = 1,
  'S',
  'C',
  'A'
}

export enum EnumResidential {
  'PRIMARY_RESIDENCE' = 1,
  'SECOND_RESIDENCE',
  'COMMUNITY',
  'NA' = 99
}

export enum EnumTension {
  '1X220' = 1,
  '1X230',
  '3X380',
  '3X380/220',
  '3X400',
  '3X400/230',
  '1X127',
  '1X133',
  '2X220',
  '2X230',
  '3X220',
  '3X220/127',
  '3X230',
  '3X230/133',
  '5000kv',
  '6000kv',
  '7200kv',
  '8000kv',
  '10000kv',
  '11000kv',
  '12000kv',
  '13000kv',
  '13200kv',
  '15000kv',
  '16500kv',
  '17000kv',
  '20000kv',
  '22000kv',
  '25000kv',
  '26500kv',
  '30000kv',
  '36000kv',
  '44000kv',
  '45000kv',
  '66000kv',
  '110000kv',
  '132000kv',
  '220000kv',
  '400000kv',
  '1200kv',
  '2000kv',
  '5500kv',
  '55000kv',
  '130000kv',
  '100kv',
  '150kv',
  '3x100',
  '3x150/260',
  '3x260'
}

export enum EnumReasonTermination {
  'TERMINATION_ACTIVITY' = 1,
  'NON_PAYMENT',
  'EXPIRATION',
  'SECURITY_ISSUE',
  'UNDEFINED_DATE',
  'SERVICE_INTERRUPTION'
}

export enum EnumReasonTerminationGas {
  'TERMINATION_ACTIVITY' = 1,
  'NON_PAYMENT',
  'EXPIRATION',
  'SECURITY_ISSUE',
  'UNDEFINED_DATE'
}

export enum EnumReasonTerminationPower {
  'TERMINATION_ACTIVITY' = 1,
  'NON_PAYMENT',
  'EXPIRATION',
  'SERVICE_INTERRUPTION' = 6 // se reemplaza por procedimiento de suspension
}

export enum EnumReasonTerminationGasValues {
  '01' = 1, // 'TERMINATION_ACTIVATED'
  '02', // 'NON_PAYMENT'
  '05', // 'EXPIRATION'
  '04', // 'SECURITY_ISSUE'
  '09' // 'UNDEFINED_DATE'
}

export enum EnumReasonTerminationPowerValues {
  '01' = 1, // 'TERMINATION_ACTIVATED'
  '04', // 'NON_PAYMENT'
  '02', // 'EXPIRATION'
  '03' = 6 // 'SERVICE_INTERRUPTION'
}

export type AttachedDocumentFile = {
  id: string
  description: string
  comments: string
  type: EnumTipoDocumento
  content: File
}

export enum EnumComplaintType {
  'GAS' = 78,
  'PWR' = 84
}

export enum EnumComplaintOwnerType {
  'GAS' = 77,
  'PWR' = 86
}

export enum EnumGetAnnexedParamsType {
  COMPLAINT_TYPE = 1,
  SUBCOMPLAINT_TYPE,
  ADMINISTRATIVE_CHANGE_GAS,
  ADMINISTRATIVE_TECHNICAL_CHANGE_GAS,
  TECHNICAL_CHANGE_GAS,
  UPDATE_OWNER_GAS,
  UPDATE_OWNER_ADM_TEC_GAS,
  ADMINISTRATIVE_CHANGE_PWR,
  ADMINISTRATIVE_TECHNICAL_CHANGE_PWR,
  NEW_WITHOUT_CHANGES,
  NEW_WITH_CHANGES,
  NEW_SUPPLY,
  ACTION_GAS,
  UPDATE_OWNER_PWR
}
